<template>
  <div class="fdd">
    <!-- Grafico delle temperature mostrato se è selezionato un Accelerometro, Inclinometro o Estensimetro -->
    <div id="fdd-plot" class="peak-wrapper noselect">
      <div class="main-wrap over-info">
        <h2>Frequency Domain Decomposition</h2>
        <!-- <p class="first-block">pippo</p> -->
        <div class="select-container">
          <ModalCalendar
            v-if="showCalendar && getDateRange[0] && getDateRange[1]"
            :datarange="[getDateRange[0], getDateRange[1]]"
            @close="showCalendar = false"
            @update="updateDate"
            @range-selected="rangeSelected"
            :maxDate="isLambro ? new Date('2021-04-23') : new Date()"
            class="modal-calendar"
            id="calendar"
          />
          <div @click="showCalendar = !showCalendar" class="info-group">
            <p class="info-btn">{{ $t("message.dateRange") }}:</p>
            <h2 class="info-btn">{{ this.formattedDate[0] + " - " + this.formattedDate[1] }}</h2>
            <i class="info-btn" v-bind:class="[showCalendar ? 'up' : 'down']"></i>
          </div>
        </div>
        <img @click="downloadFDD" class="download-btn" src="../../../../public/assets/download.svg" />
      </div>
      <div class="graph-and-info">
        <!-- Wrapper Info in Overlay -->
        <div class="infoi" v-show="!(this.getFDDBySensorType.length > 0)">
          <h2 v-if="!(this.getFDDBySensorType.length > 0)">No FDD Data...</h2>
          <h2 v-else>{{ $t("message.loading") }}...</h2>
        </div>
        <LinePlot :height="600" :traces="this.getTraces" xTitle="Date" yTitle="Frequency (Hz)" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import LinePlot from "../../graphs/LinePlot.vue";
import ModalCalendar from "../../ui/ModalCalendar";
import download from "../../../helpers/download.js";

export default {
  name: "FreqCluster",
  props: {
    sensorsType: String,
  },
  components: {
    LinePlot,
    ModalCalendar,
  },
  data() {
    return {
      actualDateRange: [],
      showCalendar: false,
      baseTrace: {
        connectgaps: true,
        line: {
          width: 4,
        },
        marker: {
          size: 8,
        },
        name: "Peaks Line",
        type: "scatter",
        mode: "lines+markers",
        xaxis: "x",
        yaxis: "y",
        opacity: 0.9,
        textfont: {},
      },
    };
  },
  computed: {
    ...mapGetters({
      getFDD: "FDD/getFDD",
      getSelectedGroupNumber: "FDD/getSelectedGroupNumber",
      getSelectedStructure: "structure/getSelectedStructure",
      getDateRange: "FDD/getFreqClusterDateRange",
      getSensors: "structure/getSensors",
    }),
    isLambro() {
      return this.getSensors ? this.getSensors.some((sn) => sn.eui === "C0EE400001015144") : false;
    },
    getCurrentSensorsByType() {
      return this.getSelectedStructure.sensors.filter((s) => s.type === this.sensorsType);
    },
    getFDDBySensorType() {
      const accTypes = ["accelerometerShortSampling", "accelerometerLongSampling", "accelerometerComplete"];
      const deckTypes = ["deckShort1600"];
      let FDD = [];
      if (this.sensorsType === "accelerometer") {
        FDD = this.getFDD.filter((fdd) => accTypes.includes(fdd.type));
      } else if (this.sensorsType === "deck") {
        FDD = this.getFDD.filter((fdd) => !fdd.type || deckTypes.includes(fdd.type));
      }
      return FDD;
    },
    getFDDOfSelectedGroup() {
      let fdd = [];
      if (this.sensorsType === "accelerometer") {
        fdd = this.getFDDBySensorType.filter((fdd) => fdd.group === this.getSelectedGroupNumber);
      } else if (this.sensorsType === "deck") {
        fdd = this.getFDDBySensorType.filter((fdd) => fdd.group === this.getSelectedGroupNumber);
      }
      return fdd;
    },
    getTraces() {
      const traces = this.getFDDOfSelectedGroup.map((i) => i.traces);
      const labels = [].concat(...traces).map((i) => i.label);
      const uniqueLabels = [...new Set(labels)];
      let basicTrace = JSON.parse(JSON.stringify(this.baseTrace));
      let tracesToPlot = uniqueLabels.map((ulab) => {
        let filteredFDD = this.getFDDOfSelectedGroup.filter((fdd) => fdd.traces.find((tr) => tr.label === ulab));
        let datesList = filteredFDD.map(function (fdd) {
          const date = new Date(fdd.date);
          date.setHours(0);
          return date;
        });
        let valuesList = filteredFDD.map((fdd) => fdd.traces.find((tr) => tr.label === ulab).value);
        return {
          ...basicTrace,
          name: ulab,
          line: { ...basicTrace.line, dash: this.checkIfValidFreq(valuesList) ? "solid" : "dashdot" },
          x: datesList,
          y: valuesList,
        };
      });
      return tracesToPlot;
    },
    formattedDate: function () {
      let firstDate = this.formatDate(this.getDateRange[0]);
      let lastDate = this.formatDate(this.getDateRange[1]);
      return [firstDate, lastDate];
    },
  },
  methods: {
    ...mapActions({
      fetchFDD: "FDD/fetchFDD",
      setDateRange: "FDD/setFreqClusterDateRange",
    }),
    updateDate() {
      this.setDateRange(this.actualDateRange);
      this.showCalendar = false;
    },
    rangeSelected(dateRange) {
      this.actualDateRange = dateRange;
    },
    formatDate(date) {
      if (date) {
        let day = date.getDate();
        let year = date.getFullYear();
        let month = date.toLocaleString("default", { month: "short" });
        return day + " " + month + " " + year;
      } else {
        return null;
      }
    },
    checkIfValidFreq(values) {
      const validPercentage = 80; // Percentuale di punti sotto 1Hz per ritenersi valida
      const under1Hz = values.filter((v) => v < 1).length;
      const total = values.length;
      return (under1Hz / total) * 100 < validPercentage;
    },
    /* Scarico .CSV */
    downloadFDD() {
      // Prendo le FDD relative al gruppo selezionati
      if (this.getFDDOfSelectedGroup.length > 0) {
        let freqsCounter = 0;
        const events = this.getFDDOfSelectedGroup.map((fdd) => {
          if (freqsCounter < fdd.freqVector.length) {
            freqsCounter = fdd.freqVector.length;
          }
          let singleEvent = {
            date: new Date(fdd.date).toLocaleDateString().replace(/,/g, ""),
            group: fdd.group,
          };
          // Genero i dati per ogni 'colonna' frequenza
          for (let i = 0; i < freqsCounter; i++) {
            singleEvent["freq" + i] = fdd.freqVector[i] || null;
          }
          return singleEvent;
        });
        let headers = {
          date: "Data (Locale)",
          group: "Group",
        };
        // Genero le 'colonne' frequenze
        for (let i = 0; i < freqsCounter; i++) {
          headers["freq" + i] = "Freq" + i + " [Hz]";
        }
        let title = "FDD";
        download.exportCSVFile(headers, events, title);
      }
    },
  },
  async mounted() {
    await this.setDateRange([this.isLambro ? new Date("2021-04-03"): new Date().setDate(new Date().getDate() - 30), this.isLambro ? new Date("2021-04-23") : new Date()]);
    this.actualDateRange = this.getDateRange;
    //this.actualDateRange2 = this.getDateRange2;
    await this.fetchFDD();
  },
  watch: {
    getSelectedStructure: {
      handler: async function () {
        await this.fetchFDD();
      },
      deep: true,
    },
    getDateRange: {
      handler: async function () {
        await this.fetchFDD();
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
/* Main Wrapper */
.fdd {
  min-width: 1000px;
  width: 92%;
  column-gap: 2%;
  display: grid;
  grid-template-columns: 49% 49%;
  margin-left: 4%;
  margin-right: 4%;
}

/* ID Evento principale */
#fdd-plot {
  width: 100%;
  min-height: 700px;
  grid-row: 2;
  grid-column-start: 1;
  grid-column-end: 3;
}

/* Wrapper Evento */
.peak-wrapper {
  position: relative;
  margin-top: 30px;
  background-color: rgb(45, 48, 65);
  -webkit-box-shadow: 0px 9px 37px -20px rgba(0, 0, 0, 0.75);
  -moz-box-shadow: 0px 9px 37px -20px rgba(0, 0, 0, 0.75);
  box-shadow: 0px 9px 37px -20px rgba(0, 0, 0, 0.75);
  border-radius: 10px;
}

/* Overlay Info */
.infoi {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  border-radius: 10px;
  background-color: black;
  z-index: 9;
  opacity: 0.7;
}
.infoi > h2 {
  margin: auto;
}

/* Info in alto sinistra sul tipo di grafico */
.over-info > h2 {
  font-size: 1em;
  color: white;
  margin-top: 0;
  margin-bottom: 0;
}
.over-info > p {
  font-size: 0.8em;
  margin-top: 0;
  margin-bottom: 0;
}
.over-info {
  text-align: left;
  position: relative;
  margin-top: 2%;
  padding-bottom: 20px;
  display: grid;
  padding-left: 60px;
  padding-right: 60px;
  border-bottom: solid 1px rgba(255, 255, 255, 0.6);
  grid-template-columns: 100%;
}

.select-container {
  color: white;
}

.info-group {
  cursor: pointer;
  display: flex;
  align-items: baseline;
}

.info-group > p {
  margin-right: 5px;
}

.download-btn {
  cursor: pointer;
  grid-row: 1/3;
  height: 18px;
  justify-self: end;
  margin-right: 32px;
  background-color: rgb(33, 150, 243);
  padding: 10px;
  grid-column: 2;
  border-radius: 10px;
}

/* Freccia */
i {
  border: solid white;
  border-width: 0 3px 3px 0;
  display: inline-block;
  padding: 5px;
  position: relative;
  margin-bottom: auto;
  margin-top: auto;
  margin-left: 10px;
  bottom: 2px;
  transition: transform 0.3s cubic-bezier(0.42, 0, 0.1, 0.96);
}
.up {
  transform: rotate(-135deg);
  -webkit-transform: rotate(-135deg);
}

.down {
  transform: rotate(45deg);
  -webkit-transform: rotate(45deg);
}

#calendar {
  position: absolute;
  top: 83px;
  max-width: 770px;
  margin-left: 5%;
  background-color: rgb(45, 48, 65);
}

.graph-and-info {
  position: relative;
}
</style>
